import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Collapse, Drawer, Form, InputNumber, List, Progress, Row, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import evmAdminService from '../../../shared/services/evmAdminService';
import { useTranslation } from '../../../shared/i18n';

const { Text } = Typography;
const ok = (r) => r && r.status === 'success';
const MODE_COLOR = { dedicated: 'blue', overcommit: 'orange', mixed: 'purple' };
const LEVEL_COLOR = { ok: 'success', warn: 'warning', stop: 'error' };
const gb = (mb) => (mb === null || mb === undefined ? '-' : `${(mb / 1024).toFixed(1)}G`);

/** Advanced policy fields: [name, label, min, max, step, tooltip]. */
const ADVANCED = [
    ['dedicated_cpu_ratio', '独享 CPU 超售比', 0.5, 8, 0.1],
    ['reserve_mb', '宿主机预留内存 MB', 0, 262144, 256, '0 = 自动 (6%, 至少 1G)'],
    ['uplink_mbps', '上联带宽 Mbps', 0, 400000, 100, '0 = 不按带宽准入'],
    ['bw_ratio', '带宽超售比', 1, 50, 1],
    ['floor_pct', '气球下限 %', 10, 100, 5, '超售实例最少保留套餐内存的比例'],
    ['headroom_pct', '客户机余量 %', 5, 50, 1],
    ['reclaim_pct', '开始回收 (可用内存 % 低于)', 10, 80, 1],
    ['relax_pct', '归还内存 (可用内存 % 高于)', 20, 95, 1],
    ['zram_pct', 'zram 大小 %', 0, 100, 5],
    ['swap_file_gb', 'SSD swap GB', 0, 512, 1],
    ['swappiness', 'swappiness', 0, 200, 10, '0 = 不修改'],
    ['psi_stop', '暂停开通 PSI %', 1, 100, 1],
    ['avail_stop_pct', '暂停开通 可用内存 %', 1, 50, 1],
    ['swap_stop_pct', '暂停开通 swap 已用 %', 5, 100, 5],
];

const PolicyDrawer = ({ node, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const mode = Form.useWatch('mode', form);

    useEffect(() => {
        if (!node) return;
        evmAdminService.getNodeOvercommit(node.id).then((r) => {
            if (!ok(r)) return;
            setData(r.data);
            form.setFieldsValue({ ...(r.data.policy || {}), mode: r.data.policy?.mode || '', disk_ratio: r.data.disk_ratio });
        });
    }, [node, form]);

    // Picking a mode for the first time fills its defaults.
    const onMode = (m) => {
        if (m && data?.defaults?.[m] && data?.policy?.mode !== m) form.setFieldsValue({ ...data.defaults[m], mode: m });
    };

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const r = await evmAdminService.saveNodeOvercommit(node.id, v);
        setSaving(false);
        if (ok(r)) { message.success(t('Saved')); onSaved(); } else message.error(r?.msg || t('Failed to save'));
    };

    const on = !!mode;
    return (
        <Drawer open={!!node} onClose={onClose} width={520} title={`${t('Overcommit policy')} · ${node?.name || ''}`}
            extra={<Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>}>
            <Form form={form} layout="vertical">
                <Form.Item name="mode" label={t('Mode')} tooltip={t('Dedicated plans go to dedicated/mixed nodes, overcommit plans to overcommit/mixed nodes')}>
                    <Select onChange={onMode} options={[
                        { value: '', label: t('Disabled') },
                        { value: 'dedicated', label: t('Dedicated') },
                        { value: 'overcommit', label: t('Overcommit') },
                        { value: 'mixed', label: t('Mixed') },
                    ]} />
                </Form.Item>
                {on && (
                    <>
                        <Row gutter={12}>
                            <Col span={8}><Form.Item name="mem_ratio" label={t('Memory overcommit ratio')} tooltip={t('Overcommit VMs')}><InputNumber min={1} max={4} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
                            <Col span={8}><Form.Item name="cpu_ratio" label={t('CPU overcommit ratio')} tooltip={t('vCPUs sold per thread (overcommit)')}><InputNumber min={1} max={16} step={0.5} style={{ width: '100%' }} /></Form.Item></Col>
                            <Col span={8}><Form.Item name="disk_ratio" label={t('Disk overcommit ratio')}><InputNumber min={1} max={10} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
                        </Row>
                        <Space size={24} wrap>
                            <Form.Item name="ksm" label="KSM" valuePropName="checked" tooltip={t('Deduplicate memory pages of same-image VMs')}><Switch /></Form.Item>
                            <Form.Item name="balloon" label={t('Balloon reclaim')} valuePropName="checked" tooltip={t('Reclaim overcommit VM memory by guest usage')}><Switch /></Form.Item>
                            <Form.Item name="swap" label="zram + swap" valuePropName="checked"><Switch /></Form.Item>
                            <Form.Item name="fair_queue" label={t('Fair queuing')} valuePropName="checked"><Switch /></Form.Item>
                        </Space>
                        <Collapse ghost items={[{
                            key: 'adv', label: t('Advanced'), children: (
                                <Row gutter={12}>
                                    {ADVANCED.map(([name, label, min, max, step, tip]) => (
                                        <Col span={12} key={name}>
                                            <Form.Item name={name} label={t(label)} tooltip={tip ? t(tip) : undefined}>
                                                <InputNumber min={min} max={max} step={step} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                    ))}
                                </Row>
                            ),
                        }]} />
                    </>
                )}
            </Form>
            {data?.suggest?.length > 0 && (
                <List size="small" header={<Text strong>{t('Suggested migrations')}</Text>} dataSource={data.suggest}
                    renderItem={(m) => <List.Item>{`${m.hostname} #${m.vm_id} · ${m.used_mb} MB → ${m.target || t('No node available')}`}</List.Item>} />
            )}
        </Drawer>
    );
};

const Overcommit = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await evmAdminService.getOvercommitReport();
        setLoading(false);
        if (ok(r)) setRows(r.data?.nodes || []);
    }, []);
    useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

    const columns = [
        { title: t('Node'), dataIndex: 'name', render: (v, r) => <Space direction="vertical" size={0}><Text>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>#{r.id}</Text></Space> },
        {
            title: t('Mode'), dataIndex: 'policy', width: 90,
            render: (p) => (p ? <Tag color={MODE_COLOR[p.mode]}>{t({ dedicated: '独享', overcommit: '超售', mixed: '混合' }[p.mode])}</Tag> : <Text type="secondary">{t('Disabled')}</Text>),
        },
        {
            title: t('Memory sold / physical'), key: 'mem', width: 190,
            render: (_, r) => (
                <Tooltip title={r.policy ? `${t('Admission used')} ${r.mem_fill_pct ?? '-'}%` : null}>
                    <div>{gb(r.mem_sold_mb)} / {gb(r.capacity?.mem_total_mb)} <Text type="secondary">({r.mem_oversell_pct ?? '-'}%)</Text></div>
                    {r.mem_fill_pct !== null && <Progress percent={Math.min(100, r.mem_fill_pct)} size="small" showInfo={false} status={r.mem_fill_pct >= 95 ? 'exception' : 'normal'} />}
                </Tooltip>
            ),
        },
        { title: t('vCPU sold / threads'), key: 'cpu', width: 120, render: (_, r) => `${r.cpu_sold} / ${r.capacity?.cpus ?? '-'}` },
        {
            title: t('Actual memory'), key: 'act', width: 130,
            render: (_, r) => <Tooltip title={`${t('Guest used')} ${gb(r.guest_used_mb)}`}>{gb(r.mem_used_mb)}</Tooltip>,
        },
        { title: t('KSM saved'), dataIndex: 'ksm_saved_mb', width: 100, render: gb },
        { title: t('Balloon reclaim'), dataIndex: 'balloon_mb', width: 100, render: gb },
        { title: 'swap', key: 'swap', width: 110, render: (_, r) => (r.swap_total_mb ? `${gb(r.swap_used_mb)} / ${gb(r.swap_total_mb)}` : '-') },
        {
            title: 'PSI', key: 'psi', width: 110,
            render: (_, r) => (r.psi ? <Tooltip title={`CPU ${r.psi.cpu?.some60 ?? '-'}% · IO ${r.psi.io?.some60 ?? '-'}%`}>{`${t('Memory')} ${r.psi.mem?.some60 ?? 0}%`}</Tooltip> : '-'),
        },
        {
            title: t('Status'), key: 'pressure', width: 110,
            render: (_, r) => (
                <Tooltip title={[...(r.pressure?.reasons || []), ...(r.errors || [])].join('；') || null}>
                    <Tag color={LEVEL_COLOR[r.pressure?.level]}>{t({ ok: '正常', warn: '偏高', stop: '暂停开通' }[r.pressure?.level] || '-')}</Tag>
                </Tooltip>
            ),
        },
        { title: '', key: 'op', width: 60, render: (_, r) => <Button size="small" icon={<SettingOutlined />} onClick={() => setEdit(r)} /> },
    ];

    return (
        <div>
            <PageHeader title={t('Overcommit')} extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />} />
            <Table size="small" rowKey="id" loading={loading} dataSource={rows} columns={columns} pagination={false} scroll={{ x: 1200 }} />
            <PolicyDrawer node={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
        </div>
    );
};

export default Overcommit;
